import type { CustomerProfileDetailsResponseDTO } from '@/types/dto/agent';
import type { UpdateCustomerProfileRequest } from '@/types/profile';
import { isoDateToApiDateTime } from '@/utils/dateTime';
import type { UpdateProfileValues } from '@/utils/zodSchema';

// Builds the profile update payload from the form values.
//
// The update is a FULL REPLACE, not a patch: any field left out of the body is
// cleared on the backend. Verified on dev CI — omitting `email` empties it, and
// omitting `preferredLanguageId` nulls it. (Consents are the exception; they are
// an additive collection with their own endpoint and survive omission.) So every
// field the form does not render has to be carried over from the stored profile,
// or saving My Information silently destroys it. See BR-57421.
//
// Empty optional *form* fields are still omitted rather than posted as '':
// posting '' for a UUID field like countryId is rejected by the backend. Since
// omission clears the field, that also gives the user a way to blank a field.
export const buildUpdateProfilePayload = (
  values: UpdateProfileValues,
  profile?: CustomerProfileDetailsResponseDTO | null
): UpdateCustomerProfileRequest => {
  const {
    email,
    birthDate,
    dietaryRequirementIds,
    seatPreferenceIds,
    ...rest
  } = values;

  const cleaned = Object.fromEntries(
    Object.entries(rest).filter(([, value]) => value !== '' && value != null)
  );

  const concessionTypeId = profile?.concessionDetails?.concessionType?.id;

  return {
    // Carried over from the stored profile: not rendered by the form, but must
    // be echoed back or the replace clears them.
    ...(profile?.preferredLanguage?.id
      ? { preferredLanguageId: profile.preferredLanguage.id }
      : {}),
    ...(profile?.isMemberOfLoyaltyProgram == null
      ? {}
      : { isMemberOfLoyaltyProgram: profile.isMemberOfLoyaltyProgram }),
    ...(concessionTypeId
      ? {
          concessionDetails: {
            concessionTypeId,
            concessionId: profile?.concessionDetails?.concessionId,
          },
        }
      : {}),
    ...cleaned,
    // Read-only in the form, but part of the replace — fall back to the stored
    // profile so a save can never blank the account's email.
    ...(email || profile?.email ? { email: email || profile?.email } : {}),
    dietaryRequirementIds,
    seatPreferenceIds,
    ...(birthDate ? { birthDate: isoDateToApiDateTime(birthDate) } : {}),
  };
};
